import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PageLoaderProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  text?: string;
}

const sizeClasses = {
  sm: 'w-6 h-6',
  md: 'w-10 h-10',
  lg: 'w-16 h-16',
};

/**
 * PageLoader - Componente de loading para uso com React Suspense
 *
 * Utilizado como fallback durante o lazy loading de rotas e componentes.
 * Exibe um spinner centralizado com animacao suave.
 */
export function PageLoader({
  className,
  size = 'lg',
  text = 'Carregando...'
}: Readonly<PageLoaderProps>) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center min-h-[400px] w-full",
        className
      )}
    >
      <div className="relative">
        {/* Halo neutro (AlvoCRM v2: sem brilho colorido) */}
        <div className="absolute inset-0 rounded-full bg-muted" />

        {/* Spinner */}
        <Loader2
          className={cn(
            "animate-spin text-primary relative z-10",
            sizeClasses[size]
          )}
        />
      </div>

      {text && (
        <p className="mt-4 text-sm text-muted-foreground animate-pulse">
          {text}
        </p>
      )}
    </div>
  );
}

/**
 * FullPageLoader - Loader para tela inteira
 *
 * Usado para transicoes de pagina completas ou carregamento inicial.
 */
export function FullPageLoader({ text }: Readonly<{ text?: string }>) {
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm z-50">
      <PageLoader size="lg" text={text} />
    </div>
  );
}

/**
 * InlineLoader - Loader compacto para uso inline
 *
 * Usado em botoes, cards ou areas menores.
 */
export function InlineLoader({ className }: Readonly<{ className?: string }>) {
  return (
    <Loader2
      className={cn(
        "w-4 h-4 animate-spin text-muted-foreground",
        className
      )}
    />
  );
}

export default PageLoader;
